import React, { useEffect, useState } from "react";
import { Grid, Paper, Typography, CircularProgress, Pagination, Box, CardHeader, CardContent, TextField, InputAdornment, IconButton, MenuItem, Select, FormControl, InputLabel, Avatar } from "@mui/material";
import { InstagramEmbed } from "react-social-media-embed";
import Card from "@mui/material/Card";
import { Crumbs } from "../../Crumbs";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import { Helmet } from "react-helmet";
import SubNav from "./SubNav.jsx"

const BACKEND_HOST = process.env["REACT_APP_BACKEND_HOST"];
const PAGE_SIZE = 9;

const CommunityInstagramPostPage = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [sort, setSort] = useState("createdAt:desc");

    useEffect(() => {
        setLoading(true);
        let url = `${BACKEND_HOST}/user-instagram-posts?pagination[page]=${page}&pagination[pageSize]=${PAGE_SIZE}&sort[0]=${sort}&populate=profile.pic`;
        if (search) {
            url += `&filters[title][$containsi]=${encodeURIComponent(search)}`;
        }
        fetch(url)
            .then(res => res.json())
            .then(obj => {
                setPosts(obj.data || []);
                setTotalPages(obj.meta?.pagination?.totalPages || 1);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [page, search, sort]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    };

    const handleSortChange = (e) => {
        setSort(e.target.value);
        setPage(1);
    };

    const desc = "Explore community Instagram posts from our dance community. Share your own posts and discover new inspiration!";
    const title = "Community Instagram Posts";

    const meta = <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta charSet="utf-8"/>
        <meta property="og:description" content={desc}/>
        <meta property="twitter:title" content={title}/>
        <meta property="twitter:description" content={desc}/>
        <title>{title}</title>
        <meta property="description" content={desc} />
        <link rel="canonical" href={"/community/instagram-posts"}/>
    </Helmet>

    if (loading) {
        return (
            <Paper style={{ padding: 20, margin: "auto" }}>
                {meta}
                <CircularProgress />
                <Typography variant="h6" align="center" style={{ marginTop: 10 }}>
                    Loading Instagram posts...
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            {meta}
            <Crumbs crumbs={[
                { title: "Community", href: "/community" },
                { title: "Instagram Posts" }
            ]} />
            <SubNav />
            <Box my={5}>
                <Typography variant="h5" align="center" gutterBottom>
                    Community Instagram Posts
                </Typography>
                <Typography variant="body1" align="center">
                    This section features Instagram posts contributed by our dance community. Enjoy discovering new inspiration and sharing your favorites!
                </Typography>
                <Box fullWidth textAlign="center" pt={3}>
                    <Button href="/community/instagram-posts/mine" variant="contained" color="secondary">
                        Add your own Instagram Posts
                    </Button>
                </Box>
                <Box mt={3} display="flex" justifyContent="center" gap={2}>
                    <form onSubmit={handleSearch}>
                        <TextField
                            size="small"
                            placeholder="Search by post title"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton type="submit" aria-label="search">
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </form>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="sort-label">Sort By</InputLabel>
                        <Select
                            labelId="sort-label"
                            value={sort}
                            label="Sort By"
                            onChange={handleSortChange}
                        >
                            <MenuItem value="createdAt:desc">Newest</MenuItem>
                            <MenuItem value="createdAt:asc">Oldest</MenuItem>
                            <MenuItem value="title:asc">Title (A-Z)</MenuItem>
                            <MenuItem value="title:desc">Title (Z-A)</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>
            <Grid container spacing={2} style={{ padding: 2 }}>
                {posts.map((post) => (
                    <Grid item xs={12} md={4} sm={6} key={post.id}>
                        <Card>
                            <CardHeader
                                title={post.title}
                                subheader={post.profile?.name || "Kind Stranger"}
                                avatar={
                                    post.profile?.pic?.formats?.thumbnail ? (
                                        <Avatar src={post.profile.pic.formats.thumbnail.url} />
                                    ) : (
                                        <Avatar>{post.profile?.name?.[0] || "?"}</Avatar>
                                    )
                                }
                            />
                            <CardContent style={{ textAlign: "center" }}>
                                <InstagramEmbed url={post.link} width={328} />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Box display="flex" justifyContent="center" mt={2}>
                <Pagination 
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                />
            </Box>
        </Box>
    );  
};  

export default CommunityInstagramPostPage;